import * as FinancialTypes from '../types/unified-financial';
import { PatientService } from './PatientService.ts'; // Adicionando .ts por consistência
import clinicInfo from '../config/clinicInfo.ts'; // Adicionando .ts por consistência
import { BillingPeriodUtils } from '../utils/billingPeriod.ts';
import { supabase } from '../config/supabase.ts';
import { ClientBankingSettingsService } from './ClientBankingSettingsService.ts';
import { toDateOnlyString, isDateOnlyString, formatDateForDisplayFromString, parseDateOnlyStringToDate } from '../utils/dateHelpers';
import type { BankingInfo } from '../types/banking';

// Alias para os tipos
type UnifiedPatient = FinancialTypes.UnifiedPatient;
type Invoice = FinancialTypes.Invoice;
type InvoiceAppointmentDetails = FinancialTypes.InvoiceAppointmentDetails;
type AppointmentDateRecord = FinancialTypes.AppointmentDateRecord;
type InvoiceItem = FinancialTypes.InvoiceItem;
type PackageInfo = FinancialTypes.PackageInfo;
type FinancialProfile = FinancialTypes.FinancialProfile;
type SessionsContracted = FinancialTypes.SessionsContracted;
type ValidityType = FinancialTypes.ValidityType;
type InvoiceChangeActor = FinancialTypes.InvoiceChangeActor;

export interface OpenInvoiceCandidate {
  invoiceId: string;
  invoiceMonthReference: string;
  invoiceTotalAmount: number;
  invoiceStatus: string;
  invoiceCreatedAt?: string | null;
  invoiceDueDate?: string | null;
  invoiceNotes?: string | null;
}

export interface NoShowChargeResolution {
  invoiceId: string;
  createdInvoice: boolean;
  invoiceItemId?: string;
}

export interface NoShowChargeInput {
  patientId: string;
  clientId: string;
  appointmentId: string;
  appointmentDate: Date;
  absenceNumber: number;
  chargeAmount: number;
  selectedInvoiceId?: string | null;
  notes?: string;
}


export interface InvoiceFilter {
  clientId?: string;
  patientId?: string;
  month?: string;
  dueMonth?: string;
  status?: 'open' | 'paid' | 'overdue' | 'cancelled';
  year?: number;
}

export interface InvoiceSummary {
  totalInvoices: number;
  totalAmount: number;
  openAmount: number;
  paidAmount: number;
  overdueAmount: number;
  byStatus: {
    open: number;
    paid: number;
    overdue: number;
    cancelled: number;
  };
}

export interface UpdateInvoiceItemInput {
  invoiceId: string;
  invoiceItemId: string;
  patientId: string;
  actor: InvoiceChangeActor;
  updates: {
    description?: string;
    amount?: number;
    date?: string;
    type?: InvoiceItem["type"];
  };
  reason: string;
}

export interface DeleteInvoiceItemInput {
  invoiceId: string;
  invoiceItemId: string;
  patientId: string;
  actor: InvoiceChangeActor;
  reason: string;
}

export interface ReplaceInvoiceItemsInput {
  invoiceId: string;
  patientId: string;
  actor: InvoiceChangeActor;
  reason: string;
  items: Array<{
    description: string;
    amount: number;
    date: string;
    type: InvoiceItem["type"];
  }>;
}

export interface ReclassifyInvoiceItemInput {
  invoiceId: string;
  invoiceItemId: string;
  patientId: string;
  actor: InvoiceChangeActor;
  targetType: "package";
  reason: string;
}

export interface DeleteInvoiceItemForAppointmentReopenInput {
  clientId: string;
  patientId: string;
  invoiceId: string;
  invoiceItemId: string;
  appointmentId: string;
  actor: InvoiceChangeActor;
  reason?: string;
}

export interface InvoiceChangeLogEntry {
  invoiceLogId: string;
  invoiceLogChangeType: 'replace_items' | 'update_item' | 'delete_item' | 'reclassify' | 'status_change';
  invoiceLogActorDisplayName?: string;
  invoiceLogActorEmail?: string;
  invoiceLogDetails?: Record<string, any>;
  invoiceLogCreatedAt: string;
}

export interface InvoiceCurrentItemValue {
  invoiceItemId: string;
  amount: number;
}

export class InvoiceService {
  private static bankingInfo: BankingInfo = ClientBankingSettingsService.getDefaultBankingInfo();
  private static bankingInfoByClient = new Map<string, BankingInfo>();

  private static calculateBillingPeriod(patient: UnifiedPatient, month: string): { startDate: Date; endDate: Date } {
    return BillingPeriodUtils.calculateBillingPeriod(patient, month);
  }

  private static formatBillingPeriod(startDate: Date, endDate: Date): string {
    return BillingPeriodUtils.formatBillingPeriod(startDate, endDate);
  }

  /**
   * Mapeia uma fatura do Supabase para a interface Invoice.
   * @param supabaseInvoice A fatura retornada pelo Supabase.
   * @returns Um objeto Invoice.
   */
  private static mapSupabaseInvoiceToInvoice(supabaseInvoice: any): Invoice {
    return {
      invoiceId: supabaseInvoice.invoiceId || supabaseInvoice.id, // Compatibilidade
      month: supabaseInvoice.invoiceMonthReference,
      type: 'individual', // A coluna 'type' não existe em 'invoices', usando padrão
      items: [], // Será preenchido separadamente da tabela 'invoice_items'
      total: supabaseInvoice.invoiceTotalAmount || 0,
      status: supabaseInvoice.invoiceStatus,
      createdAt: new Date(supabaseInvoice.invoiceCreatedAt).toISOString(),
      // Se o banco retorna DATE no formato 'YYYY-MM-DD', mantemos a string date-only
      dueDate: supabaseInvoice.invoiceDueDate
        ? (isDateOnlyString(supabaseInvoice.invoiceDueDate)
            ? supabaseInvoice.invoiceDueDate
            : toDateOnlyString(new Date(supabaseInvoice.invoiceDueDate)))
        : undefined,
      notes: supabaseInvoice.invoiceNotes,
      appointmentDetails: supabaseInvoice.appointmentDetails ? JSON.parse(supabaseInvoice.appointmentDetails) : undefined, // Permanece se ainda for JSONB no 'invoices'
      billingPeriodStart: undefined, // Não existe no schema do Supabase, é calculado
      billingPeriodEnd: undefined, // Não existe no schema do Supabase, é calculado
    };
  }

  static async getAllInvoices(filter?: InvoiceFilter): Promise<Array<Invoice & { patientName: string; patientId: string }>> {
    try {
      if (!filter?.clientId) {
        throw new Error('ClientId é obrigatório para listar faturas.');
      }

      let query = supabase
        .from('invoices')
        .select(
          `
          invoiceId,
          invoiceMonthReference,
          invoiceTotalAmount,
          invoiceStatus,
          invoiceDueDate,
          invoicePaidAt,
          invoiceNotes,
          invoiceCreatedAt,



          patientId,
          patients ( patientFullName ),
          invoice_items ( invoiceItemId, invoiceItemType, invoiceItemDescription, invoiceItemAmount, invoiceItemDate )
          `
        )
        .eq('clientId', filter.clientId);

      if (filter?.patientId) {
        query = query.eq('patientId', filter.patientId);
      }
      if (filter?.month) {
        query = query.eq('invoiceMonthReference', filter.month);
      }
      if (filter?.dueMonth) {
        const dueMonthMatch = /^(\d{4})-(\d{2})$/.exec(filter.dueMonth);
        if (dueMonthMatch) {
          const year = Number(dueMonthMatch[1]);
          const monthNumber = Number(dueMonthMatch[2]);
          const monthStart = `${dueMonthMatch[1]}-${dueMonthMatch[2]}-01`;
          const monthEnd = toDateOnlyString(new Date(year, monthNumber, 0));
          query = query
            .gte('invoiceDueDate', monthStart)
            .lte('invoiceDueDate', monthEnd);
        }
      }
      if (filter?.status) {
        query = query.eq('invoiceStatus', filter.status);
      }
      
      if (filter?.year) {
        const startOfYear = `${filter.year}-01-01T00:00:00.000Z`;
        const endOfYear = `${filter.year}-12-31T23:59:59.999Z`;
        query = query.gte('invoiceCreatedAt', startOfYear).lte('invoiceCreatedAt', endOfYear);
      }

      const { data, error } = await query.order('invoiceMonthReference', { ascending: false });

      if (error) {
        throw new Error(`Erro ao buscar faturas: ${error.message}`);
      }

      const allInvoices: Array<Invoice & { patientName: string; patientId: string }> = data.map((invoice: any) => {
        const mappedInvoice = this.mapSupabaseInvoiceToInvoice(invoice);
        
        // Mapeia os itens da fatura (preserva YYYY-MM-DD quando for date-only)
        mappedInvoice.items = invoice.invoice_items.map((item: any) => ({
          invoiceItemId: item.invoiceItemId,
          type: item.invoiceItemType,
          description: item.invoiceItemDescription,
          amount: item.invoiceItemAmount,
          date: isDateOnlyString(item.invoiceItemDate) ? item.invoiceItemDate : toDateOnlyString(new Date(item.invoiceItemDate)),
        }));
        
        // Se houver appointmentDetails na própria invoice (JSONB), parseá-lo
        if (invoice.appointmentDetails) {
          mappedInvoice.appointmentDetails = JSON.parse(invoice.appointmentDetails);
        }
        
        return {
          ...mappedInvoice,
          patientId: invoice.patientId,
          patientName: invoice.patients?.patientFullName || 'Nome Desconhecido',
        };
      });

      return allInvoices;
    } catch (error: any) {
      console.error('Erro ao buscar faturas:', error);
      throw new Error(`Erro ao buscar faturas: ${error.message}`);
    }
  }

  static async getInvoicesSummary(filter?: InvoiceFilter): Promise<InvoiceSummary> {
    try {
      const invoices = await this.getAllInvoices(filter);
      
      const summary: InvoiceSummary = {
        totalInvoices: invoices.length,
        totalAmount: 0,
        openAmount: 0,
        paidAmount: 0,
        overdueAmount: 0,
        byStatus: {
          open: 0,
          paid: 0,
          overdue: 0,
          cancelled: 0
        }
      };

      for (const invoice of invoices) {
        summary.totalAmount += invoice.total;
        if (invoice.status in summary.byStatus) {
          summary.byStatus[invoice.status as keyof typeof summary.byStatus]++;
        }

        switch (invoice.status) {
          case 'open':
          case 'pending':
            summary.openAmount += invoice.total;
            break;
          case 'paid':
            summary.paidAmount += invoice.total;
            break;
          case 'overdue':
            summary.overdueAmount += invoice.total;
            break;
        }
      }

      return summary;
    } catch (error: any) {
      console.error('Erro ao calcular resumo:', error);
      throw new Error(`Erro ao calcular resumo: ${error.message}`);
    }
  }

  static async markInvoiceAsPaid(patientId: string, invoiceId: string, clientId?: string): Promise<void> {
    try {
      if (!clientId) {
        throw new Error('ClientId é obrigatório para marcar fatura como paga.');
      }

      const { data, error } = await supabase
        .from("invoices")
        .update({
          invoiceStatus: 'paid',
          invoicePaidAt: new Date().toISOString(),
        })
        .eq("invoiceId", invoiceId)
        .eq("patientId", patientId)
        .eq("clientId", clientId)
        .select();

      if (error) {
        throw new Error(`Erro ao marcar fatura como paga: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error('Fatura não encontrada ou não pertencente ao paciente.');
      }

      console.log(`✅ Fatura ${invoiceId} marcada como paga`);
    } catch (error: any) {
      console.error('Erro ao marcar fatura como paga:', error);
      throw new Error(`Erro ao marcar fatura como paga: ${error.message}`);
    }
  }

  static async updateInvoiceStatus(
    patientId: string,
    invoiceId: string,
    status: Invoice['status'],
    clientId?: string,
    actor?: InvoiceChangeActor,
  ): Promise<void> {
    try {
      if (!clientId) {
        throw new Error('ClientId é obrigatório para atualizar status da fatura.');
      }

      const { data: currentInvoice, error: fetchError } = await supabase
        .from('invoices')
        .select()
        .eq('invoiceId', invoiceId)
        .eq('patientId', patientId)
        .eq('clientId', clientId)
        .single();

      if (fetchError) {
        throw new Error(`Erro ao buscar fatura antes da atualização: ${fetchError.message}`);
      }

      if (!currentInvoice) {
        throw new Error('Fatura não encontrada ou não pertencente ao paciente.');
      }

      const updatedPayload = {
        invoiceStatus: status,
        invoicePaidAt: status === 'paid' ? new Date().toISOString() : null,
      };

      const { data, error } = await supabase
        .from('invoices')
        .update(updatedPayload)
        .eq('invoiceId', invoiceId)
        .eq('patientId', patientId)
        .eq('clientId', clientId)
        .select();

      if (error) {
        throw new Error(`Erro ao atualizar status da fatura: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error('Fatura não encontrada ou não pertencente ao paciente.');
      }

      const newInvoice = data[0];

      if (actor) {
        await this.writeInvoiceChangeLog({
          invoice: currentInvoice,
          actor,
          changeType: 'status_change',
          details: {
            origin: 'admin_ui',
            status,
          },
          previousSnapshot: currentInvoice,
          newSnapshot: newInvoice,
        });
      }

      console.log(`✅ Status da fatura ${invoiceId} atualizado para ${status}`);
    } catch (error: any) {
      console.error('Erro ao atualizar status da fatura:', error);
      throw new Error(`Erro ao atualizar status da fatura: ${error.message}`);
    }
  }

    static async revertInvoicePayment(patientId: string, invoiceId: string, clientId?: string, actor?: InvoiceChangeActor, reason?: string): Promise<void> {
      try {
        if (!clientId) {
          throw new Error('ClientId é obrigatório para reverter pagamento da fatura.');
        }

        const { data: currentInvoice, error: fetchError } = await supabase
          .from('invoices')
          .select()
          .eq('invoiceId', invoiceId)
          .eq('patientId', patientId)
          .eq('clientId', clientId)
          .single();

        if (fetchError) {
          throw new Error(`Erro ao buscar fatura antes de reverter: ${fetchError.message}`);
        }

        if (!currentInvoice) {
          throw new Error('Fatura não encontrada ou não pertencente ao paciente.');
        }

        if (currentInvoice.invoiceStatus !== 'paid') {
          throw new Error('A fatura não está marcada como paga.');
        }

        const { data: lastLog, error: lastLogError } = await supabase
          .from('invoice_change_log')
          .select('invoiceLogPreviousSnapshot, invoiceLogNewSnapshot, invoiceLogCreatedAt')
          .eq('invoiceId', invoiceId)
          .eq('invoiceLogChangeType', 'status_change')
          .order('invoiceLogCreatedAt', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (lastLogError) {
          throw new Error(`Erro ao buscar histórico de auditoria: ${lastLogError.message}`);
        }

        let targetStatus: Invoice['status'] | null = null;
        if (lastLog && lastLog.invoiceLogPreviousSnapshot && lastLog.invoiceLogPreviousSnapshot.invoiceStatus) {
          targetStatus = lastLog.invoiceLogPreviousSnapshot.invoiceStatus;
        }

        if (!targetStatus) {
          const due = currentInvoice.dueDate ? new Date(currentInvoice.dueDate) : null;
          if (due && due < new Date()) targetStatus = 'overdue';
          else targetStatus = 'open';
        }

        const { data: updatedData, error: updateError } = await supabase
          .from('invoices')
          .update({
            invoiceStatus: targetStatus,
            invoicePaidAt: null,
          })
          .eq('invoiceId', invoiceId)
          .eq('patientId', patientId)
          .eq('clientId', clientId)
          .select();

        if (updateError) {
          throw new Error(`Erro ao reverter status da fatura: ${updateError.message}`);
        }

        if (!updatedData || updatedData.length === 0) {
          throw new Error('Fatura não encontrada após tentativa de reverter.');
        }

        const newInvoice = updatedData[0];

        if (actor) {
          const details: Record<string, any> = {
            origin: 'admin_ui',
            action: 'revert_paid',
          };
          if (reason) details.reason = reason;

          await this.writeInvoiceChangeLog({
            invoice: currentInvoice,
            actor,
            changeType: 'status_change',
            details,
            previousSnapshot: currentInvoice,
            newSnapshot: newInvoice,
          });
        }

        console.log(`✅ Fatura ${invoiceId} revertida para ${targetStatus}`);
      } catch (error: any) {
        console.error('Erro ao reverter pagamento da fatura:', error);
        throw new Error(`Erro ao reverter pagamento da fatura: ${error.message}`);
      }
    }

    static async cancelInvoice(patientId: string, invoiceId: string, reason?: string, clientId?: string): Promise<void> {
    try {
      if (!clientId) {
        throw new Error('ClientId é obrigatório para cancelar fatura.');
      }

      const { data, error } = await supabase
        .from("invoices")
        .update({
          invoiceStatus: 'cancelled',
          invoiceNotes: reason ? `Cancelada: ${reason}` : 'Cancelada',
        })
        .eq("invoiceId", invoiceId)
        .eq("patientId", patientId)
        .eq("clientId", clientId)
        .select();

      if (error) {
        throw new Error(`Erro ao cancelar fatura: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error('Fatura não encontrada ou não pertencente ao paciente.');
      }

      console.log(`✅ Fatura ${invoiceId} cancelada`);
    } catch (error: any) {
      console.error('Erro ao cancelar fatura:', error);
      throw new Error(`Erro ao cancelar fatura: ${error.message}`);
    }
  }

  static async getInvoiceById(invoiceId: string, clientId: string): Promise<{ invoice: Invoice; patient: UnifiedPatient } | null> {
    try {
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('invoices')
        .select(
          `
          invoiceId,
          invoiceMonthReference,
          invoiceTotalAmount,
          invoiceStatus,
          invoiceDueDate,
          invoicePaidAt,
          invoiceNotes,
          invoiceCreatedAt,
          appointmentDetails,
          patientId,
          invoice_items ( invoiceItemId, invoiceItemType, invoiceItemDescription, invoiceItemAmount, invoiceItemDate ),
          patients ( patientFullName, patientCpf, patientDateOfBirth, patientGender, patientAddressStreet, patientAddressNumber, patientAddressComplement, patientAddressNeighborhood, patientAddressCity, patientAddressState, patientAddressZipCode, patientEmergencyContactName, patientEmergencyContactPhone, patientEmergencyContactRelationship, patientMedicalInfoBloodType, patientMedicalInfoAllergies, patientMedicalInfoMedications, patientMedicalInfoConditions, patientCreatedAt, patientUpdatedAt )
          `
        )
        .eq('invoiceId', invoiceId)
        .eq('clientId', clientId) // Adicionar filtro por clientId
        .single();

      if (invoiceError) {
        console.error(`Erro ao buscar fatura: ${invoiceError.message}`);
        return null;
      }

      if (!invoiceData) {
        return null; 
      }

      // Criar o objeto UnifiedPatient diretamente dos dados da consulta
      const patient: UnifiedPatient = {
        id: invoiceData.patientId,
        personalData: {
          name: invoiceData.patients[0].patientFullName, // Acessar o primeiro elemento do array
          cpf: invoiceData.patients[0].patientCpf,      // Acessar o primeiro elemento do array
          birthDate: invoiceData.patients[0].patientDateOfBirth, // Acessar o primeiro elemento do array
          // Outros campos de personalData podem ser adicionados conforme necessário
        },
        financialProfile: {} as any, // Será preenchido por PatientService.getPatientById ou em outro lugar
        appointments: [], // Será preenchido por PatientService.getPatientById ou em outro lugar
        invoices: [], // Será preenchido por PatientService.getPatientById ou em outro lugar
        createdAt: invoiceData.patients[0].patientCreatedAt, // Acessar o primeiro elemento do array
        updatedAt: invoiceData.patients[0].patientUpdatedAt,   // Acessar o primeiro elemento do array
      };

      // Buscar o restante do perfil financeiro e appointments
      const fullPatient = await PatientService.getPatientById(invoiceData.patientId, clientId);
      if (fullPatient) {
        patient.financialProfile = fullPatient.financialProfile;
        patient.appointments = fullPatient.appointments;
        patient.invoices = fullPatient.invoices;
      }

      // Mapeia os itens da fatura
      const items: Invoice['items'] = invoiceData.invoice_items.map((item: any) => ({
        invoiceItemId: item.invoiceItemId,
        type: item.invoiceItemType,
        description: item.invoiceItemDescription,
        amount: item.invoiceItemAmount,
        date: isDateOnlyString(item.invoiceItemDate) ? item.invoiceItemDate : toDateOnlyString(new Date(item.invoiceItemDate)),
      }));

      const invoice: Invoice = {
        invoiceId: invoiceData.invoiceId,
        month: invoiceData.invoiceMonthReference,
        type: 'individual',
        items: items,
        total: invoiceData.invoiceTotalAmount,
        status: invoiceData.invoiceStatus,
        createdAt: new Date(invoiceData.invoiceCreatedAt).toISOString(),
        // Preservar DATE como 'YYYY-MM-DD' quando for esse o caso; caso contrário, gerar ISO
        dueDate: invoiceData.invoiceDueDate
          ? (isDateOnlyString(invoiceData.invoiceDueDate)
              ? invoiceData.invoiceDueDate
              : toDateOnlyString(new Date(invoiceData.invoiceDueDate)))
          : undefined,
        notes: invoiceData.invoiceNotes,
        paidAt: invoiceData.invoicePaidAt ? new Date(invoiceData.invoicePaidAt).toISOString() : undefined,
        appointmentDetails: invoiceData.appointmentDetails ? JSON.parse(invoiceData.appointmentDetails) : undefined,
      };

      return { invoice, patient };
    } catch (error: any) {
      console.error('Erro ao buscar fatura:', error);
      throw new Error(`Erro ao buscar fatura: ${error.message}`);
    }
  }

  /**
   * Gera uma cobrança (item de fatura) para um único agendamento concluído.
   * Se não houver uma fatura aberta para o mês, uma nova é criada.
   */
  static async generateForSingleAppointment(patientId: string, appointmentId: string): Promise<void> {
    try {
      // 1. Obter os dados necessários do paciente e do agendamento
      const { data: appointmentData, error: aptError } = await supabase
        .from("appointments")
        .select("appointmentStartDate, clientId")
        .eq("appointmentId", appointmentId)
        .single();

      if (aptError || !appointmentData) {
        throw new Error(`Agendamento ${appointmentId} não encontrado.`);
      }

      const { data: patientData, error: patientError } = await supabase
        .from("patients")
        .select("financial_profiles!inner(financialProfileIndividualSessionPrice)")
        .eq("patientId", patientId)
        .single();

      if (patientError || !patientData?.financial_profiles[0]?.financialProfileIndividualSessionPrice) {
        throw new Error(`Perfil financeiro ou preço individual não encontrado para o paciente ${patientId}.`);
      }

      const sessionPrice = patientData.financial_profiles[0].financialProfileIndividualSessionPrice;
      const clientId = appointmentData.clientId;
      const appointmentDate = new Date(appointmentData.appointmentStartDate);
      const monthReference = `${appointmentDate.getFullYear()}-${String(appointmentDate.getMonth() + 1).padStart(2, '0')}`;

      // 2. Verificar se já existe uma fatura aberta para este paciente no mês de referência
      let { data: invoice, error: invoiceError } = await supabase
        .from("invoices")
        .select("invoiceId, invoiceTotalAmount")
        .eq("patientId", patientId)
        .eq("clientId", clientId)
        .eq("invoiceMonthReference", monthReference)
        .eq("invoiceStatus", "open")
        .single();

      let invoiceId: string;
      let currentTotal = 0;

      if (invoiceError && invoiceError.code !== "PGRST116") { // PGRST116 = no rows found
        throw invoiceError;
      }

      if (invoice) {
        // 3a. Se a fatura existe, usamos o ID e o total dela
        invoiceId = invoice.invoiceId;
        currentTotal = invoice.invoiceTotalAmount;
        console.log(`🧾 Fatura ${invoiceId} encontrada para o mês ${monthReference}.`);
      } else {
        // 3b. Se não existe, criamos uma nova fatura
        console.log(`🧾 Nenhuma fatura encontrada para ${monthReference}. Criando uma nova...`);
        const { data: newInvoice, error: newInvoiceError } = await supabase
          .from("invoices")
          .insert({
            clientId: clientId,
            patientId: patientId,
            invoiceMonthReference: monthReference,
            invoiceTotalAmount: 0, // O total será atualizado depois
            invoiceStatus: "open",
            invoiceDueDate: toDateOnlyString(new Date(appointmentDate.getFullYear(), appointmentDate.getMonth() + 1, 10)), // Vencimento dia 10 do prox mês
          })
          .select("invoiceId")
          .single();
        
        if (newInvoiceError || !newInvoice) throw newInvoiceError ?? new Error("Falha ao criar nova fatura.");
        invoiceId = newInvoice.invoiceId;
      }

      // 4. Adicionar o item da sessão à fatura
      const { error: itemError } = await supabase.from("invoice_items").insert({
        invoiceId: invoiceId,
        clientId: clientId,
        invoiceItemDescription: `Sessão de Fisioterapia - ${appointmentDate.toLocaleDateString("pt-BR")}`,
        invoiceItemAmount: sessionPrice,
        invoiceItemType: "session",
        invoiceItemDate: toDateOnlyString(appointmentDate),
      });

      if (itemError) throw itemError;

      // 5. Atualizar o valor total da fatura
      const { error: updateTotalError } = await supabase
        .from("invoices")
        .update({ invoiceTotalAmount: currentTotal + sessionPrice })
        .eq("invoiceId", invoiceId);

      if (updateTotalError) throw updateTotalError;

      console.log(`✅ Item de sessão adicionado à fatura ${invoiceId} com sucesso! Novo total: ${currentTotal + sessionPrice}`);

    } catch (error: any) {

      console.error("❌ Erro ao gerar cobrança para sessão avulsa:", error);
      // Em um app real, poderíamos ter uma lógica de retry ou notificação aqui
      throw new Error(`Falha ao gerar cobrança: ${error.message}`);
    }
  }

  static async generateAndSaveInvoice(
    patientId: string,
    clientId: string, // Adicionar clientId
    month: string,
    closingData?: {
      packageAmount: number;
      individualSessionsAmount: number;
      extraSessionsAmount: number;
      absenceCharges: number;
      appointments: any[];
    }
  ): Promise<Invoice> {
    try {
      // Obter dados completos do paciente e perfil financeiro
      const { data: patientData, error: patientError } = await supabase
        .from("patients")
        .select(
          `
          patientId,
          patientFullName,
          patientCreatedAt,
          patientUpdatedAt,
          financial_profiles!inner ( financialProfileId, financialProfileHasPackage, financialProfileIndividualSessionPrice )
          `
        )
        .eq("patientId", patientId)
        .eq("clientId", clientId) // Filtrar por clientId
        .single();

      if (patientError || !patientData) {
        throw new Error(`Paciente ou perfil financeiro não encontrado para o clientId ${clientId}: ${patientError?.message}`);
      }

      // Para manter a compatibilidade com a função calculateBillingPeriod, podemos criar um objeto 'patient' parcial
      const patient: UnifiedPatient = {
        id: patientData.patientId,
        personalData: {
          name: patientData.patientFullName,
          // Preencher outros campos de personalData do patientData conforme necessário
        },
        financialProfile: {
          financialProfileId: patientData.financial_profiles?.[0]?.financialProfileId,
          hasPackage: patientData.financial_profiles?.[0]?.financialProfileHasPackage,
          individualSessionPrice: patientData.financial_profiles?.[0]?.financialProfileIndividualSessionPrice,
          // Preencher outros campos de financialProfile do patientData conforme necessário
        } as any,
        appointments: [], // Será preenchido por PatientService.getPatientById ou em outro lugar
        invoices: [],     // Será preenchido por PatientService.getPatientById ou em outro lugar
        createdAt: patientData.patientCreatedAt || new Date().toISOString(), // Supondo que patientCreatedAt exista ou fornecer um padrão
        updatedAt: patientData.patientUpdatedAt || new Date().toISOString(), // Supondo que patientUpdatedAt exista ou fornecer um padrão
      };

      const { startDate, endDate } = this.calculateBillingPeriod(patient, month);
      const billingPeriodStart = startDate.toISOString();
      const billingPeriodEnd = endDate.toISOString();

      console.log(`📅 Período de faturamento para ${patient.personalData.name}: ${this.formatBillingPeriod(startDate, endDate)}`);

      const invoiceItemsToInsert: Array<{
        invoiceItemType: string;
        invoiceItemDescription: string;
        invoiceItemAmount: number;
        invoiceItemDate: string;
      }> = [];
      const items: InvoiceItem[] = []; // Adicionar esta linha
      let total = 0;
      let invoiceType: Invoice['type'] = 'individual';
      let appointmentDetails: InvoiceAppointmentDetails | undefined = undefined;

      if (closingData) {
        if (closingData.appointments && closingData.appointments.length > 0) {
          appointmentDetails = {
            completed: [],
            cancelled: [],
            noShow: [],
            scheduled: []
          };

          closingData.appointments.forEach(apt => {
            const record: AppointmentDateRecord = {
              date: apt.start ? (isDateOnlyString(apt.start) ? apt.start : toDateOnlyString(new Date(apt.start))) : (apt.date || toDateOnlyString(new Date())),
              status: apt.status,
              description: apt.title || apt.subject || 'Sessão de fisioterapia'
            };

            switch (apt.status) {
              case 'completed':
                appointmentDetails!.completed.push(record);
                break;
              case 'cancelled':
                appointmentDetails!.cancelled.push(record);
                break;
              case 'no_show':
                appointmentDetails!.noShow.push(record);
                break;
              case 'scheduled':
                appointmentDetails!.scheduled.push(record);
                break;
            }
          });

          appointmentDetails.completed.sort((a, b) => a.date.localeCompare(b.date));
          appointmentDetails.cancelled.sort((a, b) => a.date.localeCompare(b.date));
          appointmentDetails.noShow.sort((a, b) => a.date.localeCompare(b.date));
          appointmentDetails.scheduled.sort((a, b) => a.date.localeCompare(b.date));
        }

        // Lógica de pacotes
        if (patientData.financial_profiles?.[0]?.financialProfileHasPackage) {
          const { data: activePackage, error: packageError } = await supabase
            .from("packages")
            .select("*", { count: 'exact' })
            .eq("financialProfileId", patientData.financial_profiles[0].financialProfileId)
            .eq("clientId", clientId)
            .is("packageEndDate", null) // Ou outra lógica para pacote ativo
            .single();

          if (packageError) {
            console.error("Erro ao buscar pacote ativo:", packageError.message);
            // Decidir como lidar: continuar sem pacote ou lançar erro
          }

          if (activePackage) {
            invoiceType = 'package';
            const packagePrice = activePackage.packagePrice || 0;
            invoiceItemsToInsert.push({ // Usar invoiceItemsToInsert
              invoiceItemDate: month + '-01',
              invoiceItemDescription: `Pacote ${activePackage.packageName || 'Fisioterapia'}`, 
              invoiceItemAmount: packagePrice,
              invoiceItemType: 'package'
            });

            total += packagePrice;

            // Lógica para sessões adicionais do pacote
            const { count: completedSessionsCount, error: sessionsCountError } = await supabase
              .from("appointments")
              .select("appointmentId", { count: 'exact' })
              .eq("patientId", patientId)
              .eq("clientId", clientId)
              .eq("appointmentStatus", "completed")
              .gte("appointmentStartDate", billingPeriodStart)
              .lte("appointmentStartDate", billingPeriodEnd);

            if (sessionsCountError) {
              console.error("Erro ao contar sessões concluídas para pacote:", sessionsCountError.message);
            }

            const numCompletedSessions = completedSessionsCount || 0;
            const sessionsContracted = activePackage.packageSessionsContracted || 0;
            const extraSessions = Math.max(0, numCompletedSessions - sessionsContracted);

            if (extraSessions > 0) {
              const effectiveAdditionalSessionPrice = activePackage.packageAdditionalSessionPrice || patientData.financial_profiles[0].financialProfileIndividualSessionPrice || 170;
              const extraAmount = extraSessions * effectiveAdditionalSessionPrice;
              total += extraAmount;

              invoiceItemsToInsert.push({
                invoiceItemDate: month + '-01',
                invoiceItemDescription: `Sessões adicionais (${extraSessions} sessão${extraSessions !== 1 ? 'ões' : ''} além do pacote)`,
                invoiceItemAmount: extraAmount,
                invoiceItemType: 'additional'
              });
            }
          }
        } else { // Sem pacote ou pacote inativo, cobrar por sessões individuais
          const { count: completedSessionsCount, error: sessionsCountError } = await supabase
            .from("appointments")
            .select("appointmentId", { count: 'exact' })
            .eq("patientId", patientId)
            .eq("clientId", clientId)
            .eq("appointmentStatus", "completed")
            .gte("appointmentStartDate", billingPeriodStart)
            .lte("appointmentStartDate", billingPeriodEnd);

          if (sessionsCountError) {
            console.error("Erro ao contar sessões concluídas para individual:", sessionsCountError.message);
          }

          const numCompletedSessions = completedSessionsCount || 0;
          const effectiveIndividualSessionPrice = patientData.financial_profiles?.[0]?.financialProfileIndividualSessionPrice || 0;
          const individualSessionsAmount = numCompletedSessions * effectiveIndividualSessionPrice;

          if (individualSessionsAmount > 0) {
            invoiceItemsToInsert.push({
              invoiceItemDate: month + '-01',
              invoiceItemDescription: `Sessões individuais (${numCompletedSessions} sessões)`,
              invoiceItemAmount: individualSessionsAmount,
              invoiceItemType: 'session'
            });
            total += individualSessionsAmount;
          }
        }

        // Lógica para cobranças por faltas (se houver no closingData)
        if (closingData.absenceCharges > 0) {
          invoiceItemsToInsert.push({
            invoiceItemDate: month + '-01',
            invoiceItemDescription: 'Cobranças por faltas',
            invoiceItemAmount: closingData.absenceCharges,
            invoiceItemType: 'other'
          });
          total += closingData.absenceCharges;
        }
      } else {
        // Caso não haja closingData, a lógica para gerar fatura legada ainda é chamada.
        // Esta parte também precisaria ser revista para usar o novo esquema do Supabase.
        // Passar clientId para generateInvoiceForPatient
        const invoice = await this.generateInvoiceForPatient(patientId, clientId, month);
        // O generateInvoiceForPatient já retorna a fatura no formato correto, então podemos usar seus items diretamente
        invoice.items.forEach(item => invoiceItemsToInsert.push({
          invoiceItemDate: item.date,
          invoiceItemDescription: item.description,
          invoiceItemAmount: item.amount,
          invoiceItemType: item.type as any // Cast para o tipo correto se necessário
        }));
        total = invoice.total;
        invoiceType = invoice.type;
        // Se houver appointmentDetails na fatura gerada, copiá-los
        if (invoice.appointmentDetails) {
          appointmentDetails = invoice.appointmentDetails;
        }
      }
      const { data: newInvoiceData, error: insertError } = await supabase
        .from('invoices')
        .insert({
          patientId: patientId,
          invoiceMonthReference: month,
          invoiceTotalAmount: total,
          invoiceStatus: 'open',
          invoiceCreatedAt: new Date().toISOString(),
          invoiceDueDate: toDateOnlyString(new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)),
          invoiceNotes: `Fatura gerada automaticamente em ${new Date().toLocaleDateString('pt-BR')}`,
          appointmentDetails: appointmentDetails ? JSON.stringify(appointmentDetails) : null,
          billingPeriodStart: billingPeriodStart,
          billingPeriodEnd: billingPeriodEnd
        })
        .select()
        .single();

      if (insertError) {
        throw new Error(`Erro ao salvar fatura no Supabase: ${insertError.message}`);
      }

      const invoiceItemsWithInvoiceId = invoiceItemsToInsert.map(item => ({
        ...item,
        invoiceId: newInvoiceData.invoiceId
      }));

      const { error: insertItemsError } = await supabase
        .from('invoice_items')
        .insert(invoiceItemsWithInvoiceId);

      if (insertItemsError) {
        throw new Error(`Erro ao salvar itens da fatura no Supabase: ${insertItemsError.message}`);
      }

      const createdInvoice: Invoice = {
        invoiceId: newInvoiceData.invoiceId,
        month: newInvoiceData.invoiceMonthReference,
        type: invoiceType,
        items: invoiceItemsToInsert.map(item => ({
          date: item.invoiceItemDate, // Mapeia para date
          description: item.invoiceItemDescription, // Mapeia para description
          amount: item.invoiceItemAmount, // Mapeia para amount
          type: item.invoiceItemType as Invoice["items"][number]["type"]
        })),
        total: newInvoiceData.invoiceTotalAmount,
        status: newInvoiceData.invoiceStatus,
        createdAt: new Date(newInvoiceData.invoiceCreatedAt).toISOString(),
  dueDate: isDateOnlyString(newInvoiceData.invoiceDueDate) ? newInvoiceData.invoiceDueDate : toDateOnlyString(new Date(newInvoiceData.invoiceDueDate)),
        notes: newInvoiceData.invoiceNotes,
        appointmentDetails: newInvoiceData.appointmentDetails ? JSON.parse(newInvoiceData.appointmentDetails) : undefined,
        billingPeriodStart: newInvoiceData.billingPeriodStart,
        billingPeriodEnd: newInvoiceData.billingPeriodEnd,
      };

      console.log(`✅ Fatura ${createdInvoice.invoiceId} criada e salva para ${patient.personalData.name}`);

      if (closingData?.appointments) {
        const { IntegrationService } = await import('./IntegrationService');
        for (const apt of closingData.appointments) {
          if (apt.status === 'completed') {
            await IntegrationService.markAppointmentAsBilled(apt.id, createdInvoice.invoiceId, clientId);
          }
        }
      }

      return createdInvoice;
    } catch (error: any) {
      console.error('Erro ao gerar e salvar fatura:', error);
      throw new Error(`Erro ao gerar e salvar fatura: ${error.message}`);
    }
  }

  private static async ensureFinancialStructureExists(patientId: string, clientId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from("financial_profiles")
        .select("financialProfileId")
        .eq("patientId", patientId)
        .eq("clientId", clientId)
        .single();

      if (error && error.code === "PGRST116") {
        console.warn(`⚠️ Perfil financeiro para paciente ${patientId} no tenant ${clientId} não encontrado. Criando um padrão...`);
        const { error: insertError } = await supabase
          .from("financial_profiles")
          .insert({
            patientId: patientId,
            clientId: clientId,
            financialProfileHasPackage: false,
            financialProfileIndividualSessionPrice: 0,
            financialProfileIsActive: true,
            financialProfileRenewalStatus: 'active',
          });

        if (insertError) {
          throw new Error(`Erro ao criar perfil financeiro padrão: ${insertError.message}`);
        }
        console.log(`✅ Perfil financeiro padrão criado para paciente ${patientId} no tenant ${clientId}`);
      } else if (error) {
        throw new Error(`Erro ao verificar perfil financeiro: ${error.message}`);
      }
    } catch (error: any) {
      console.error(`❌ Erro ao garantir estrutura financeira para o paciente ${patientId} no tenant ${clientId}:`, error);
      throw new Error(`Falha ao garantir estrutura financeira: ${error.message}`);
    }
  }

  static async generateInvoiceForPatient(patientId: string, clientId: string, month: string): Promise<Invoice> {
    try {
      const { data: patientData, error: patientError } = await supabase
        .from("patients")
        .select(
          `
          patientId,
          patientFullName,
          patientCreatedAt,
          patientUpdatedAt,
          financial_profiles!inner ( financialProfileId, financialProfileHasPackage, financialProfileIndividualSessionPrice ),
          packages!left ( * )
          `
        )
        .eq("patientId", patientId)
        .eq("clientId", clientId)
        .single();

      if (patientError || !patientData) {
        throw new Error(`Paciente ou perfil financeiro não encontrado para o clientId ${clientId}: ${patientError?.message}`);
      }
      const patient: UnifiedPatient = {
        id: patientData.patientId,
        personalData: { name: patientData.patientFullName },
        financialProfile: {
          financialProfileId: patientData.financial_profiles?.[0]?.financialProfileId,
          hasPackage: patientData.financial_profiles?.[0]?.financialProfileHasPackage,
          individualSessionPrice: patientData.financial_profiles?.[0]?.financialProfileIndividualSessionPrice,
          packageInfo: patientData.packages?.[0] ? {
            // Mapeamento correto para o novo tipo PackageInfo
            packageId: patientData.packages[0].packageId,
            clientId: patientData.packages[0].clientId,
            financialProfileId: patientData.packages[0].financialProfileId,
            packageName: patientData.packages[0].packageName,
            packagePrice: patientData.packages[0].packagePrice,
            packageSessionsContracted: patientData.packages[0].packageSessionsContracted,
            packageSessionsUsed: patientData.packages[0].packageSessionsUsed,
            packageSessionsRemaining: patientData.packages[0].packageSessionsRemaining,
            packageValidityType: patientData.packages[0].packageValidityType as ValidityType,
            packageCustomValidityMonths: patientData.packages[0].packageCustomValidityMonths,
            packagePaymentDate: patientData.packages[0].packagePaymentDate ?? new Date().toISOString(),
            packageRenewalDate: patientData.packages[0].packageRenewalDate,
            packageNotes: patientData.packages[0].packageNotes,
            packageCreatedAt: patientData.packages[0].packageCreatedAt ?? new Date().toISOString(),
          } : undefined,
        } as FinancialProfile,
        appointments: [], 
        invoices: [],     
        createdAt: patientData.patientCreatedAt ? new Date(patientData.patientCreatedAt).toISOString() : new Date().toISOString(),
        updatedAt: patientData.patientUpdatedAt ? new Date(patientData.patientUpdatedAt).toISOString() : new Date().toISOString(),
      };

      const { startDate, endDate } = this.calculateBillingPeriod(patient, month);
      const billingPeriodStart = startDate.toISOString();
      const billingPeriodEnd = endDate.toISOString();

      const items: Invoice["items"] = [];
      let total = 0;
      let invoiceType: Invoice["type"] = "individual";

      // Buscar agendamentos concluídos para o período
      const { data: appointmentsData, error: appointmentsError } = await supabase
        .from("appointments")
        .select("appointmentStartDate, appointmentStatus, appointmentTitle, appointmentResolutionNotes")
        .eq("patientId", patientId)
        .eq("clientId", clientId)
        .eq("appointmentStatus", "completed")
        .gte("appointmentStartDate", billingPeriodStart)
        .lte("appointmentStartDate", billingPeriodEnd);

      if (appointmentsError) {
        console.error("Erro ao buscar agendamentos concluídos:", appointmentsError.message);
        // Decidir como lidar com o erro
      }

      const monthlyAppointments = appointmentsData || [];

      if (patient.financialProfile.hasPackage && patient.financialProfile.packageInfo) {
        const packageInfo = patient.financialProfile.packageInfo;
        invoiceType = "package";
        total = packageInfo.packagePrice; // Corrigido

        items.push({
          date: packageInfo.packagePaymentDate || month + "-01", // Corrigido
          description: `${packageInfo.packageName} - ${packageInfo.packageSessionsContracted} sessões`, // Corrigido
          amount: packageInfo.packagePrice, // Corrigido
          type: "package"
        });

        const extraSessions = Math.max(0, monthlyAppointments.length - packageInfo.packageSessionsContracted); // Corrigido
        if (extraSessions > 0) {
          // O campo additionalSessionPrice não existe mais, usamos o preço individual como fallback.
          const effectiveAdditionalSessionPrice = patient.financialProfile.individualSessionPrice || 170;
          const extraAmount = extraSessions * effectiveAdditionalSessionPrice;
          total += extraAmount;

          items.push({
            date: endDate.toISOString(),
            description: `${extraSessions} sessões adicionais`,
            amount: extraAmount,
            type: "additional"
          });
        }
      } else {
        const effectiveIndividualSessionPrice = patient.financialProfile.individualSessionPrice || 0;
        total = monthlyAppointments.length * effectiveIndividualSessionPrice;

        monthlyAppointments.forEach(apt => {
          items.push({
            date: toDateOnlyString(new Date(apt.appointmentStartDate)),
            description: `Sessão de fisioterapia${apt.appointmentResolutionNotes ? ` - ${apt.appointmentResolutionNotes}` : ""}`,
            amount: effectiveIndividualSessionPrice,
            type: "session"
          });
        });
      }

      const invoice: Invoice = {
        invoiceId: `inv_${Date.now()}`,
        month,
        type: invoiceType,
        items,
        total,
        status: "open",
        createdAt: new Date().toISOString(),
  dueDate: toDateOnlyString(new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)),
        billingPeriodStart,
        billingPeriodEnd
      };

      return invoice;

    } catch (error: any) {
      console.error("Erro ao gerar fatura (legado):", error);
      throw new Error(`Erro ao gerar fatura (legado): ${error.message}`);
    }
  }




  
  static formatInvoiceForWhatsApp(invoice: Invoice & { patientName: string }, includePaymentInfo: boolean = true): string {
    const formatDate = (dateStr: string | undefined) => {
      return formatDateForDisplayFromString(dateStr);
    };

    const formatCurrency = (amount: number) => {
      return amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    };

    let message = "";

    message += `🏥 *${clinicInfo.nomeFantasia.toUpperCase()}*\n`;
    message += `${clinicInfo.especialidades}\n`;
    message += `CNPJ: ${clinicInfo.cnpj}\n`;
    message += `📋 *FATURA DE ATENDIMENTOS*\n\n`;

    message += `👤 *Paciente:* ${invoice.patientName}\n`;

    if (invoice.billingPeriodStart && invoice.billingPeriodEnd) {
      const startDate = formatDate(invoice.billingPeriodStart);
      const endDate = formatDate(invoice.billingPeriodEnd);
      message += `📅 *Período:* ${startDate} a ${endDate}\n`;
    } else {
      message += `📅 *Período:* ${invoice.month}\n`;
    }

    message += `🆔 *Fatura:* ${invoice.invoiceId}\n\n`;

    if (invoice.type === "package") {
      message += `📦 *PACOTE CONTRATADO*\n`;
    } else if (invoice.type === "renewal") {
      message += `🔄 *RENOVAÇÃO DE PACOTE*\n`;
    } else {
      message += `💼 *SESSÕES AVULSAS*\n`;
    }

    message += `━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

    if (invoice.appointmentDetails) {
      message += `📆 *HISTÓRICO DE ATENDIMENTOS:*\n\n`;

      if (invoice.appointmentDetails.completed.length > 0) {
        message += `✅ *Sessões Realizadas (${invoice.appointmentDetails.completed.length}):*\n`;
        invoice.appointmentDetails.completed.forEach((apt, index) => {
          message += `${index + 1}. ${formatDate(apt.date)} - ${apt.description}\n`;
        });
        message += `\n`;
      }

      if (invoice.appointmentDetails.noShow.length > 0) {
        message += `❌ *Faltas (${invoice.appointmentDetails.noShow.length}):*\n`;
        invoice.appointmentDetails.noShow.forEach((apt, index) => {
          message += `${index + 1}. ${formatDate(apt.date)} - ${apt.description}\n`;
        });
        message += `\n`;
      }

      if (invoice.appointmentDetails.cancelled.length > 0) {
        message += `🚫 *Cancelamentos (${invoice.appointmentDetails.cancelled.length}):*\n`;
        invoice.appointmentDetails.cancelled.forEach((apt, index) => {
          message += `${index + 1}. ${formatDate(apt.date)} - ${apt.description}\n`;
        });
        message += `\n`;
      }

      message += `━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    }

    message += `📝 *DETALHAMENTO FINANCEIRO:*\n\n`;

    invoice.items.forEach((item, index) => {
      const itemDate = formatDate(item.date);
      const itemAmount = formatCurrency(item.amount);
      message += `${index + 1}. ${itemDate}\n`;
      message += `   ${item.description}\n`;
      message += `   ${itemAmount}\n\n`;
    });

    message += `━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `💰 *TOTAL A PAGAR: ${formatCurrency(invoice.total)}*\n`;
    message += `📅 *Vencimento:* ${formatDate(invoice.dueDate ?? 'Data não informada')}`;

    if (includePaymentInfo) {
      message += `🏦 *DADOS PARA PAGAMENTO:*\n\n`;
      message += `🏛️ *Banco:* ${this.bankingInfo.bankName}\n`;
      message += `🏢 *Beneficiário:* ${this.bankingInfo.beneficiary}\n`;
      message += `🔢 *Agência:* ${this.bankingInfo.agency}\n`;
      message += `💳 *Conta:* ${this.bankingInfo.account}\n`;
      message += `📱 *PIX (${this.bankingInfo.pixType ?? 'Não Informado'}):* ${this.bankingInfo.pixKey}\n\n`;
    }

    message += `━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `📞 Entre em contato para dúvidas\n`;
    message += `🙏 Obrigado pela confiança!`;

    return message;
  }

  static async loadBankingInfo(clientId?: string): Promise<BankingInfo> {
    if (!clientId) {
      return this.getBankingInfo();
    }

    const bankingInfo = await ClientBankingSettingsService.getByClientId(clientId);
    this.setBankingInfo(bankingInfo, clientId);
    return bankingInfo;
  }

  static setBankingInfo(bankingInfo: BankingInfo, clientId?: string): void {
    this.bankingInfo = bankingInfo;
    if (clientId) {
      this.bankingInfoByClient.set(clientId, bankingInfo);
    }
  }

  static getBankingInfo(clientId?: string): BankingInfo {
    if (clientId && this.bankingInfoByClient.has(clientId)) {
      return { ...this.bankingInfoByClient.get(clientId)! };
    }

    return { ...this.bankingInfo };
  }

  static async getOpenInvoicesForPatientMonth(
    patientId: string,
    clientId: string,
    monthReference: string,
  ): Promise<OpenInvoiceCandidate[]> {
    const { data, error } = await supabase
      .from('invoices')
      .select('invoiceId, invoiceMonthReference, invoiceTotalAmount, invoiceStatus, invoiceCreatedAt, invoiceDueDate, invoiceNotes')
      .eq('patientId', patientId)
      .eq('clientId', clientId)
      .eq('invoiceMonthReference', monthReference)
      .eq('invoiceStatus', 'open')
      .order('invoiceCreatedAt', { ascending: true });

    if (error) {
      throw new Error(`Erro ao buscar faturas abertas para o mês ${monthReference}: ${error.message}`);
    }

    return (data ?? []).map((invoice: any) => ({
      invoiceId: invoice.invoiceId,
      invoiceMonthReference: invoice.invoiceMonthReference,
      invoiceTotalAmount: Number(invoice.invoiceTotalAmount || 0),
      invoiceStatus: invoice.invoiceStatus,
      invoiceCreatedAt: invoice.invoiceCreatedAt || null,
      invoiceDueDate: invoice.invoiceDueDate || null,
      invoiceNotes: invoice.invoiceNotes || null,
    }));
  }

  static async createOrAppendNoShowCharge(input: NoShowChargeInput): Promise<NoShowChargeResolution> {
    const {
      patientId,
      clientId,
      appointmentId,
      appointmentDate,
      absenceNumber,
      chargeAmount,
      selectedInvoiceId,
      notes,
    } = input;

    if (chargeAmount <= 0) {
      throw new Error('A cobrança por falta precisa ter valor positivo.');
    }

    const monthReference = `${appointmentDate.getFullYear()}-${String(appointmentDate.getMonth() + 1).padStart(2, '0')}`;
    const itemDate = toDateOnlyString(appointmentDate);
    const itemDescription = `Cobrança por falta - Falta #${absenceNumber} - ${appointmentDate.toLocaleDateString('pt-BR')}`;
    const openInvoices = await this.getOpenInvoicesForPatientMonth(patientId, clientId, monthReference);

    let invoiceId: string | null = null;
    let createdInvoice = false;

    if (selectedInvoiceId) {
      const selectedInvoice = openInvoices.find((invoice) => invoice.invoiceId === selectedInvoiceId);
      if (!selectedInvoice) {
        throw new Error('A fatura selecionada não está aberta ou não pertence ao mês atual.');
      }
      invoiceId = selectedInvoice.invoiceId;
    } else if (openInvoices.length === 1) {
      invoiceId = openInvoices[0].invoiceId;
    } else if (openInvoices.length === 0) {
      const dueDate = new Date(appointmentDate.getFullYear(), appointmentDate.getMonth() + 1, 10);
      const { data: newInvoice, error: newInvoiceError } = await supabase
        .from('invoices')
        .insert({
          clientId,
          patientId,
          invoiceMonthReference: monthReference,
          invoiceTotalAmount: 0,
          invoiceStatus: 'open',
          invoiceCreatedAt: new Date().toISOString(),
          invoiceDueDate: toDateOnlyString(dueDate),
          invoiceNotes: notes || 'Cobrança automática de falta registrada na agenda',
        })
        .select('invoiceId')
        .single();

      if (newInvoiceError || !newInvoice) {
        throw new Error(`Falha ao criar nova fatura para cobrança de falta: ${newInvoiceError?.message || 'erro desconhecido'}`);
      }

      invoiceId = newInvoice.invoiceId;
      createdInvoice = true;
    } else {
      throw new Error('MÚLTIPLAS_FATURAS_ABERTAS');
    }

    if (!invoiceId) {
      throw new Error('Não foi possível determinar a fatura para cobrança de falta.');
    }

    const { data: existingItem, error: existingItemError } = await supabase
      .from('invoice_items')
      .select('invoiceItemId')
      .eq('invoiceId', invoiceId)
      .eq('clientId', clientId)
      .eq('invoiceItemType', 'other')
      .eq('invoiceItemDate', itemDate)
      .eq('invoiceItemDescription', itemDescription)
      .eq('invoiceItemAmount', chargeAmount)
      .maybeSingle();

    if (existingItemError) {
      throw new Error(`Erro ao verificar item de falta já lançado: ${existingItemError.message}`);
    }

    if (!existingItem) {
      const { error: insertItemError } = await supabase
        .from('invoice_items')
        .insert({
          invoiceId,
          clientId,
          invoiceItemDescription: itemDescription,
          invoiceItemAmount: chargeAmount,
          invoiceItemType: 'other',
          invoiceItemDate: itemDate,
        });

      if (insertItemError) {
        throw new Error(`Erro ao salvar cobrança por falta: ${insertItemError.message}`);
      }
    }

    await this.recalculateAndPersistInvoiceTotal(invoiceId, clientId);

    return {
      invoiceId,
      createdInvoice,
      invoiceItemId: existingItem?.invoiceItemId,
    };
  }

  /**
   * Encontra uma fatura aberta ou cria uma nova para então adicionar um item.
   * Centraliza a lógica de faturamento para pacotes, sessões avulsas e créditos.
   */
  static async findOrCreateInvoiceAndAddItem(
    itemDetails: {
      patientId: string;
      clientId: string;
      description: string;
      amount: number;
      type: 'package' | 'session' | 'additional' | 'other';
      date?: Date;
    }
  ): Promise<{ invoiceId: string }> {
    const { patientId, clientId, description, amount, type, date } = itemDetails;
    const itemDate = date || new Date();

    console.log(`🧾 Iniciando faturamento de item [${type}] para o paciente ${patientId}...`);

    try {
      // 1. Verificar se já existe QUALQUER fatura com status 'open' ou 'pending' para o paciente.
      // Ordenamos pela data de criação para pegar a mais recente, caso haja mais de uma.
      let { data: invoice, error: invoiceError } = await supabase
        .from("invoices")
        .select("invoiceId, invoiceTotalAmount")
        .eq("patientId", patientId)
        .eq("clientId", clientId)
        .in("invoiceStatus", ["open", "pending"])
        .order("invoiceCreatedAt", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (invoiceError) {
        throw invoiceError;
      }

      let invoiceId: string;
      let currentTotal = 0;

      if (invoice) {
        // 2a. Se a fatura existe, usamos o ID e o total dela
        invoiceId = invoice.invoiceId;
        currentTotal = invoice.invoiceTotalAmount;
        console.log(`   - Fatura existente (${invoiceId}) encontrada com total de ${currentTotal}. O novo item será adicionado a ela.`);
      } else {
        // 2b. Se não existe, criamos uma nova fatura para o mês atual
        const monthReference = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}`;
        console.log(`   - Nenhuma fatura aberta encontrada. Criando uma nova para o mês ${monthReference}...`);
        const dueDate = new Date(itemDate.getFullYear(), itemDate.getMonth() + 1, 10);

        const { data: newInvoice, error: newInvoiceError } = await supabase
          .from("invoices")
          .insert({
            clientId: clientId,
            patientId: patientId,
            invoiceMonthReference: monthReference,
            invoiceTotalAmount: 0, // O total será atualizado depois
            invoiceStatus: "open",
            invoiceDueDate: toDateOnlyString(dueDate),
          })
          .select("invoiceId")
          .single();

        if (newInvoiceError || !newInvoice) {
          throw newInvoiceError ?? new Error("Falha ao criar nova fatura.");
        }
        invoiceId = newInvoice.invoiceId;
        console.log(`   - Nova fatura ${invoiceId} criada.`);
      }

      // 3. Adicionar o item à fatura
      const { error: itemError } = await supabase.from("invoice_items").insert({
        invoiceId: invoiceId,
        clientId: clientId,
        invoiceItemDescription: description,
        invoiceItemAmount: amount,
        invoiceItemType: type,
  invoiceItemDate: toDateOnlyString(itemDate),
      });

      if (itemError) throw itemError;

      // 4. Atualizar o valor total da fatura
      const newTotal = currentTotal + amount;
      const { error: updateTotalError } = await supabase
        .from("invoices")
        .update({ invoiceTotalAmount: newTotal })
        .eq("invoiceId", invoiceId);

      if (updateTotalError) throw updateTotalError;

      console.log(`✅ Item "${description}" adicionado à fatura ${invoiceId}. Novo total: ${newTotal}`);

      return { invoiceId };

    } catch (error: any) {

      console.error(`❌ Erro ao processar item de fatura para o paciente ${patientId}:`, error);
      throw new Error(`Falha ao processar item de fatura: ${error.message}`);
    }
  }
  
  static async getPatientInvoices(patientId: string): Promise<Invoice[]> {
    try {
      const { data, error } = await supabase
        .from("invoices")
        .select(
          `
          invoiceId,
          invoiceMonthReference,
          invoiceTotalAmount,
          invoiceStatus,
          invoiceDueDate,
          invoicePaidAt,
          invoiceNotes,
          invoiceCreatedAt,
          appointmentDetails
          `
        )
        .eq("patientId", patientId)
        .order("invoiceMonthReference", { ascending: false });

      if (error) {
        throw new Error(`Erro ao buscar faturas do paciente: ${error.message}`);
      }

      const patientInvoices: Invoice[] = data.map((invoice: any) => ({
        ...this.mapSupabaseInvoiceToInvoice(invoice),
        type: 'individual', // A coluna 'type' não existe em 'invoices', usando padrão
        items: invoice.invoiceItems ? JSON.parse(invoice.invoiceItems) : [],
        appointmentDetails: invoice.appointmentDetails ? JSON.parse(invoice.appointmentDetails) : undefined,
      }));

      return patientInvoices;
    } catch (error: any) {
      console.error('Erro ao buscar faturas do paciente:\n',
        error);
      throw new Error(`Erro ao buscar faturas: ${error.message}`);
    }
  }

  static async getOverdueInvoices(): Promise<Array<Invoice & { patientName: string; patientId: string }>> {
    try {
  const today = toDateOnlyString(new Date());
      const { data, error } = await supabase
        .from("invoices")
        .select(
          `
          invoiceId,
          invoiceMonthReference,
          invoiceTotalAmount,
          invoiceStatus,
          invoiceDueDate,
          invoicePaidAt,
          invoiceNotes,
          invoiceCreatedAt,
          patientId,
          patients ( patientFullName )
          `
        )
        .filter("invoiceStatus", "in", '("open", "pending")')
        .lt("invoiceDueDate", today);

      if (error) {
        throw new Error(`Erro ao buscar faturas vencidas: ${error.message}`);
      }

      const overdueInvoices: Array<Invoice & { patientName: string; patientId: string }> = data.map((invoice: any) => ({
        ...this.mapSupabaseInvoiceToInvoice(invoice),
        patientId: invoice.patientId,
        patientName: invoice.patients?.patientFullName || 'Nome Desconhecido',
      }));

      return overdueInvoices;
    } catch (error: any) {
      console.error('Erro ao buscar faturas vencidas:\n',
        error);
      throw new Error(`Erro ao buscar faturas vencidas: ${error.message}`);
    }
  }

  static async getInvoiceChangeLogs(params: {
    invoiceId: string;
    patientId: string;
    actor: InvoiceChangeActor;
  }): Promise<InvoiceChangeLogEntry[]> {
    const { invoiceId, patientId, actor } = params;

    const { data, error } = await supabase
      .from('invoice_change_log')
      .select(`
        invoiceLogId,
        invoiceLogChangeType,
        invoiceLogActorDisplayName,
        invoiceLogActorEmail,
        invoiceLogDetails,
        invoiceLogCreatedAt
      `)
      .eq('invoiceId', invoiceId)
      .eq('patientId', patientId)
      .eq('clientId', actor.clientId)
      .order('invoiceLogCreatedAt', { ascending: false });

    if (error) {
      throw new Error(`Erro ao buscar histórico de alterações da fatura: ${error.message}`);
    }

    return (data ?? []) as InvoiceChangeLogEntry[];
  }

  static async getInvoiceItemsCurrentValues(params: {
    invoiceId: string;
    patientId: string;
    actor: InvoiceChangeActor;
  }): Promise<InvoiceCurrentItemValue[]> {
    const { invoiceId, patientId, actor } = params;

    const { data: invoice, error: invoiceError } = await supabase
      .from('invoices')
      .select('invoiceId, clientId')
      .eq('invoiceId', invoiceId)
      .eq('patientId', patientId)
      .eq('clientId', actor.clientId)
      .single();

    if (invoiceError || !invoice) {
      throw new Error('Fatura não encontrada para validar alterações pendentes.');
    }

    const { data: rows, error } = await supabase
      .from('invoice_items')
      .select('invoiceItemId, invoiceItemAmount')
      .eq('invoiceId', invoiceId)
      .eq('clientId', actor.clientId);

    if (error) {
      throw new Error(`Erro ao validar alterações pendentes da fatura: ${error.message}`);
    }

    return (rows ?? []).map((row: any) => ({
      invoiceItemId: row.invoiceItemId,
      amount: Number(row.invoiceItemAmount || 0),
    }));
  }

  private static readonly EDITABLE_INVOICE_STATUS = new Set(['open', 'pending']);
  private static readonly ITEM_TYPES = new Set<InvoiceItem['type']>(['session', 'package', 'additional', 'other']);

  private static assertAdminActor(actor: InvoiceChangeActor, clientId: string): void {
    if (actor.userRole !== 'admin') {
      throw new Error('Apenas administradores podem alterar faturas.');
    }
    if (!actor.userId) {
      throw new Error('Usuário executor inválido para auditoria.');
    }
    if (actor.clientId !== clientId) {
      throw new Error('O usuário executor não pertence ao tenant da fatura.');
    }
  }

  private static assertInvoiceIsEditable(status: string): void {
    if (!this.EDITABLE_INVOICE_STATUS.has(status)) {
      throw new Error('Somente faturas com status open ou pending podem ser alteradas.');
    }
  }

  private static normalizeDateOnly(input: string): string {
    if (!input) {
      throw new Error('Data do item é obrigatória.');
    }
    if (isDateOnlyString(input)) {
      return input;
    }

    const parsed = new Date(input);
    if (Number.isNaN(parsed.getTime())) {
      throw new Error(`Data inválida: ${input}`);
    }
    return toDateOnlyString(parsed);
  }

  private static validateInvoiceItemInput(item: {
    description: string;
    amount: number;
    date: string;
    type: InvoiceItem['type'];
  }): { description: string; amount: number; date: string; type: InvoiceItem['type'] } {
    const description = item.description?.trim();
    const date = this.normalizeDateOnly(item.date);
    const amount = Number(item.amount);

    if (!description) {
      throw new Error('A descrição do item é obrigatória.');
    }
    if (!Number.isFinite(amount)) {
      throw new Error('O valor do item é inválido.');
    }
    if (amount < 0) {
      throw new Error('O valor do item não pode ser negativo.');
    }
    if (!this.ITEM_TYPES.has(item.type)) {
      throw new Error('O tipo do item é inválido.');
    }

    return {
      description,
      amount,
      date,
      type: item.type,
    };
  }

  private static async fetchEditableInvoice(invoiceId: string, patientId: string, clientId: string): Promise<any> {
    const { data: invoice, error } = await supabase
      .from('invoices')
      .select(`
        invoiceId,
        clientId,
        patientId,
        invoiceStatus,
        invoiceTotalAmount,
        invoice_items (
          invoiceItemId,
          invoiceItemType,
          invoiceItemDescription,
          invoiceItemAmount,
          invoiceItemDate
        )
      `)
      .eq('invoiceId', invoiceId)
      .eq('patientId', patientId)
      .eq('clientId', clientId)
      .single();

    if (error || !invoice) {
      throw new Error('Fatura não encontrada para edição.');
    }

    this.assertInvoiceIsEditable(invoice.invoiceStatus);
    return invoice;
  }

  private static buildInvoiceSnapshot(invoice: any): {
    invoice: {
      invoiceId: string;
      clientId: string;
      patientId: string;
      invoiceStatus: string;
      invoiceTotalAmount: number;
    };
    items: Array<{
      invoiceItemId: string;
      invoiceItemType: string;
      invoiceItemDescription: string;
      invoiceItemAmount: number;
      invoiceItemDate: string;
    }>;
  } {
    const items = (invoice.invoice_items ?? [])
      .map((item: any) => ({
        invoiceItemId: item.invoiceItemId,
        invoiceItemType: item.invoiceItemType,
        invoiceItemDescription: item.invoiceItemDescription,
        invoiceItemAmount: Number(item.invoiceItemAmount || 0),
        invoiceItemDate: this.normalizeDateOnly(item.invoiceItemDate),
      }))
      .sort((a: any, b: any) => {
        const byDate = a.invoiceItemDate.localeCompare(b.invoiceItemDate);
        if (byDate !== 0) return byDate;
        return (a.invoiceItemId || '').localeCompare(b.invoiceItemId || '');
      });

    return {
      invoice: {
        invoiceId: invoice.invoiceId,
        clientId: invoice.clientId,
        patientId: invoice.patientId,
        invoiceStatus: invoice.invoiceStatus,
        invoiceTotalAmount: Number(invoice.invoiceTotalAmount || 0),
      },
      items,
    };
  }

  private static async recalculateAndPersistInvoiceTotal(invoiceId: string, clientId: string): Promise<number> {
    if (!clientId) {
      throw new Error('ClientId é obrigatório para recalcular total da fatura.');
    }

    const { data: rows, error } = await supabase
      .from('invoice_items')
      .select('invoiceItemAmount')
      .eq('invoiceId', invoiceId)
      .eq('clientId', clientId);

    if (error) {
      throw new Error(`Erro ao recalcular total da fatura: ${error.message}`);
    }

    const total = (rows ?? []).reduce((acc: number, row: any) => acc + Number(row.invoiceItemAmount || 0), 0);

    if (total < 0) {
      throw new Error('A fatura não pode terminar com total negativo.');
    }

    const { error: updateError } = await supabase
      .from('invoices')
      .update({ invoiceTotalAmount: total })
      .eq('invoiceId', invoiceId)
      .eq('clientId', clientId);

    if (updateError) {
      throw new Error(`Erro ao persistir total da fatura: ${updateError.message}`);
    }

    return total;
  }

  private static async writeInvoiceChangeLog(params: {
    invoice: any;
    actor: InvoiceChangeActor;
    changeType: 'replace_items' | 'update_item' | 'delete_item' | 'reclassify' | 'status_change';
    details: Record<string, any>;
    previousSnapshot: any;
    newSnapshot: any;
  }): Promise<void> {
    const { invoice, actor, changeType, details, previousSnapshot, newSnapshot } = params;

    const { error } = await supabase
      .from('invoice_change_log')
      .insert({
        clientId: invoice.clientId,
        invoiceId: invoice.invoiceId,
        patientId: invoice.patientId,
        actorUserId: actor.userId,
        invoiceLogActorDisplayName: actor.displayName ?? null,
        invoiceLogActorEmail: actor.email ?? null,
        invoiceLogChangeType: changeType,
        invoiceLogDetails: details,
        invoiceLogPreviousSnapshot: previousSnapshot,
        invoiceLogNewSnapshot: newSnapshot,
      });

    if (error) {
      throw new Error(`Erro ao gravar auditoria da alteração: ${error.message}`);
    }
  }

  private static async findInvoiceAppointmentsByDate(params: {
    clientId: string;
    patientId: string;
    invoiceId: string;
    itemDate: string;
  }): Promise<Array<{ appointmentId: string; appointmentStartDate: string }>> {
    const { clientId, patientId, invoiceId, itemDate } = params;

    const { data, error } = await supabase
      .from('appointments')
      .select('appointmentId, appointmentStartDate')
      .eq('clientId', clientId)
      .eq('patientId', patientId)
      .eq('invoiceId', invoiceId)
      .eq('appointmentIsBilled', true)
      .in('appointmentStatus', ['completed', 'no_evaluated']);

    if (error) {
      throw new Error(`Erro ao buscar agendamento faturado: ${error.message}`);
    }

    const normalizedDate = this.normalizeDateOnly(itemDate);
    return (data ?? []).filter((appointment: any) => {
      return this.normalizeDateOnly(appointment.appointmentStartDate) === normalizedDate;
    });
  }

  private static async releaseBilledAppointment(appointmentId: string, clientId: string): Promise<void> {
    const { error } = await supabase
      .from('appointments')
      .update({
        appointmentStatus: 'no_billed',
        appointmentIsBilled: false,
        invoiceId: null,
        appointmentBillingDate: null,
        appointmentPaymentOrigin: null,
        appointmentUpdatedAt: new Date().toISOString(),
      })
      .eq('appointmentId', appointmentId)
      .eq('clientId', clientId);

    if (error) {
      throw new Error(`Erro ao atualizar agendamento após remoção do item: ${error.message}`);
    }
  }

  private static validityMonthsFromPackage(pkg: any): number {
    switch (pkg.packageValidityType) {
      case '1_MONTH':
        return 1;
      case '2_MONTHS':
        return 2;
      case '3_MONTHS':
        return 3;
      case 'CUSTOM':
        return Number(pkg.packageCustomValidityMonths || 0);
      default:
        return 0;
    }
  }

  private static addMonthsClamped(baseDate: Date, monthsToAdd: number): Date {
    const targetMonthStart = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth() + monthsToAdd,
      1
    );

    const lastDayOfTargetMonth = new Date(
      targetMonthStart.getFullYear(),
      targetMonthStart.getMonth() + 1,
      0
    ).getDate();

    const clampedDay = Math.min(baseDate.getDate(), lastDayOfTargetMonth);

    return new Date(
      targetMonthStart.getFullYear(),
      targetMonthStart.getMonth(),
      clampedDay
    );
  }

  private static packageIsValidForDate(pkg: any, referenceDate: string): boolean {
    const dateToValidate = parseDateOnlyStringToDate(this.normalizeDateOnly(referenceDate));
    if (Number.isNaN(dateToValidate.getTime())) {
      return false;
    }

    const renewalDate = this.toDateOnlyFromAny(pkg.packageRenewalDate);
    if (renewalDate) {
      const renewalDateValue = parseDateOnlyStringToDate(renewalDate);
      if (!Number.isNaN(renewalDateValue.getTime())) {
        return renewalDateValue >= dateToValidate;
      }
    }

    if (!pkg.packagePaymentDate) {
      return false;
    }

    const paymentDate = parseDateOnlyStringToDate(this.normalizeDateOnly(pkg.packagePaymentDate));
    if (Number.isNaN(paymentDate.getTime())) {
      return false;
    }

    const validityMonths = this.validityMonthsFromPackage(pkg);
    if (validityMonths <= 0) {
      return dateToValidate >= paymentDate;
    }

    const endDate = this.addMonthsClamped(paymentDate, validityMonths);
    return endDate >= dateToValidate;
  }

  private static currentDateOnly(): string {
    return toDateOnlyString(new Date());
  }

  private static async findEligiblePackageForReclassify(params: {
    clientId: string;
    patientId: string;
    referenceDate: string;
  }): Promise<any> {
    const { clientId, patientId, referenceDate } = params;

    const { data: profile, error: profileError } = await supabase
      .from('financial_profiles')
      .select('financialProfileId')
      .eq('clientId', clientId)
      .eq('patientId', patientId)
      .single();

    if (profileError || !profile) {
      throw new Error('Perfil financeiro não encontrado para reclassificação.');
    }

    const { data: packages, error: packageError } = await supabase
      .from('packages')
      .select(`
        packageId,
        packageSessionsUsed,
        packageSessionsRemaining,
        packageValidityType,
        packageCustomValidityMonths,
        packagePaymentDate,
        packageRenewalDate
      `)
      .eq('clientId', clientId)
      .eq('financialProfileId', profile.financialProfileId)
      .order('packagePaymentDate', { ascending: false });

    if (packageError) {
      throw new Error(`Erro ao buscar pacote para reclassificação: ${packageError.message}`);
    }

    const normalizedReferenceDate = this.normalizeDateOnly(referenceDate);
    const eligible = (packages ?? []).find((pkg: any) => {
      const remaining = Number(pkg.packageSessionsRemaining || 0);
      return remaining > 0 && this.packageIsValidForDate(pkg, normalizedReferenceDate);
    });

    if (!eligible) {
      const diagnostics = (packages ?? []).map((pkg: any) => {
        const remaining = Number(pkg.packageSessionsRemaining || 0);
        const paymentDate = this.toDateOnlyFromAny(pkg.packagePaymentDate) ?? 'n/a';
        const renewalDate = this.toDateOnlyFromAny(pkg.packageRenewalDate) ?? 'n/a';
        const validityMonths = this.validityMonthsFromPackage(pkg);
        const hasCredits = remaining > 0;
        const validForDate = hasCredits && this.packageIsValidForDate(pkg, normalizedReferenceDate);

        let reason = 'ok';
        if (!hasCredits) {
          reason = 'sem créditos disponíveis';
        } else if (!this.packageIsValidForDate(pkg, normalizedReferenceDate)) {
          reason = 'fora da janela de validade para a data atual';
        }

        return `pacote=${pkg.packageId} | credits=${remaining} | paymentDate=${paymentDate} | renewalDate=${renewalDate} | validityType=${pkg.packageValidityType ?? 'n/a'} | validityMonths=${validityMonths} | validNow=${validForDate ? 'yes' : 'no'} | reason=${reason}`;
      });

      const diagnosticLog = diagnostics.length > 0
        ? diagnostics.join('\n')
        : 'Nenhum pacote encontrado para o perfil financeiro do paciente.';

      throw new Error(
        `Paciente sem pacote válido com sessões disponíveis para a data atual da reclassificação.\n` +
        `Diagnóstico (referenceDate=${normalizedReferenceDate}):\n` +
        diagnosticLog
      );
    }

    return eligible;
  }

  private static toDateOnlyFromAny(value: any): string | null {
    if (!value) {
      return null;
    }
    try {
      return this.normalizeDateOnly(String(value));
    } catch {
      return null;
    }
  }

  private static moneyEquals(a: number, b: number): boolean {
    return Math.abs(a - b) < 0.01;
  }

  private static async assertPackageItemCanBeDeleted(params: {
    clientId: string;
    patientId: string;
    targetItem: any;
  }): Promise<{ packageId: string; contracted: number; remaining: number } | null> {
    const { clientId, patientId, targetItem } = params;
    if (targetItem.invoiceItemType !== 'package') {
      return null;
    }

    const { data: profile, error: profileError } = await supabase
      .from('financial_profiles')
      .select('financialProfileId')
      .eq('clientId', clientId)
      .eq('patientId', patientId)
      .single();

    if (profileError || !profile) {
      throw new Error('Perfil financeiro não encontrado para validar exclusão de item de pacote.');
    }

    const { data: packages, error: packageError } = await supabase
      .from('packages')
      .select(`
        packageId,
        packagePrice,
        packagePaymentDate,
        packageCreatedAt,
        packageSessionsContracted,
        packageSessionsRemaining
      `)
      .eq('clientId', clientId)
      .eq('financialProfileId', profile.financialProfileId)
      .order('packageCreatedAt', { ascending: false });

    if (packageError) {
      throw new Error(`Erro ao validar pacote para exclusão: ${packageError.message}`);
    }
    if (!packages || packages.length === 0) {
      throw new Error('Pacote associado ao item não encontrado para validação da exclusão.');
    }

    const itemAmount = Number(targetItem.invoiceItemAmount || 0);
    const itemDate = this.toDateOnlyFromAny(targetItem.invoiceItemDate);

    const ranked = packages
      .map((pkg: any) => {
        let score = 0;

        if (this.moneyEquals(Number(pkg.packagePrice || 0), itemAmount)) {
          score += 2;
        }

        const paymentDate = this.toDateOnlyFromAny(pkg.packagePaymentDate);
        const createdDate = this.toDateOnlyFromAny(pkg.packageCreatedAt);
        if (itemDate && paymentDate === itemDate) {
          score += 3;
        }
        if (itemDate && createdDate === itemDate) {
          score += 2;
        }

        return { pkg, score };
      })
      .sort((a: any, b: any) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        const aCreated = new Date(a.pkg.packageCreatedAt || 0).getTime();
        const bCreated = new Date(b.pkg.packageCreatedAt || 0).getTime();
        return bCreated - aCreated;
      });

    const candidate = ranked[0]?.pkg;
    if (!candidate) {
      throw new Error('Pacote associado ao item não encontrado para validação da exclusão.');
    }

    const contracted = Number(candidate.packageSessionsContracted || 0);
    const remaining = Number(candidate.packageSessionsRemaining || 0);

    if (remaining < contracted) {
      throw new Error('O paciente já utilizou seções deste pacote e não possui crédito suficiente para a exclusão.');
    }

    return {
      packageId: candidate.packageId,
      contracted,
      remaining,
    };
  }

  private static async consumePackageSession(params: {
    packageId: string;
    appointmentId: string;
    clientId: string;
  }): Promise<{ packageId: string }> {
    const { packageId, appointmentId, clientId } = params;

    const { data: pkg, error: packageError } = await supabase
      .from('packages')
      .select('packageId, packageSessionsUsed, packageSessionsRemaining')
      .eq('packageId', packageId)
      .eq('clientId', clientId)
      .single();

    if (packageError || !pkg) {
      throw new Error('Pacote não encontrado para consumo.');
    }
    if ((pkg.packageSessionsRemaining ?? 0) <= 0) {
      throw new Error('Pacote sem sessões disponíveis para consumo.');
    }

    const { error: updateError } = await supabase
      .from('packages')
      .update({
        packageSessionsUsed: Number(pkg.packageSessionsUsed || 0) + 1,
        packageSessionsRemaining: Number(pkg.packageSessionsRemaining || 0) - 1,
      })
      .eq('packageId', packageId)
      .eq('clientId', clientId);

    if (updateError) {
      throw new Error(`Erro ao atualizar contadores do pacote: ${updateError.message}`);
    }

    const { error: usageError } = await supabase
      .from('package_session_usage')
      .insert({
        clientId,
        packageId,
        appointmentId,
        usageStatus: 'active',
        usageNotes: 'Consumo criado por reclassificação de item de invoice.',
      });

    if (usageError) {
      throw new Error(`Erro ao registrar consumo da sessão no pacote: ${usageError.message}`);
    }

    return { packageId };
  }

  static async updateInvoiceItem(input: UpdateInvoiceItemInput): Promise<{ invoiceId: string; total: number }> {
    const { invoiceId, invoiceItemId, patientId, actor, updates, reason } = input;
    if (!reason?.trim()) {
      throw new Error('Justificativa é obrigatória para alteração do item.');
    }

    const beforeInvoice = await this.fetchEditableInvoice(invoiceId, patientId, actor.clientId);
    this.assertAdminActor(actor, beforeInvoice.clientId);

    const targetItem = (beforeInvoice.invoice_items ?? []).find((item: any) => item.invoiceItemId === invoiceItemId);
    if (!targetItem) {
      throw new Error('Item da fatura não encontrado.');
    }

    const nextType = (updates.type ?? targetItem.invoiceItemType) as InvoiceItem['type'];
    const previousType = targetItem.invoiceItemType as InvoiceItem['type'];
    if ((previousType === 'session' && nextType === 'package') || (previousType === 'package' && nextType === 'session')) {
      throw new Error('Use a operação de reclassificação para trocar entre sessão avulsa e pacote.');
    }

    const normalizedItem = this.validateInvoiceItemInput({
      description: updates.description ?? targetItem.invoiceItemDescription,
      amount: updates.amount ?? Number(targetItem.invoiceItemAmount || 0),
      date: updates.date ?? targetItem.invoiceItemDate,
      type: nextType,
    });

    const { error: updateError } = await supabase
      .from('invoice_items')
      .update({
        invoiceItemDescription: normalizedItem.description,
        invoiceItemAmount: normalizedItem.amount,
        invoiceItemDate: normalizedItem.date,
        invoiceItemType: normalizedItem.type,
      })
      .eq('invoiceItemId', invoiceItemId)
      .eq('invoiceId', invoiceId)
      .eq('clientId', beforeInvoice.clientId);

    if (updateError) {
      throw new Error(`Erro ao atualizar item da fatura: ${updateError.message}`);
    }

    const total = await this.recalculateAndPersistInvoiceTotal(invoiceId, beforeInvoice.clientId);
    const afterInvoice = await this.fetchEditableInvoice(invoiceId, patientId, actor.clientId);

    await this.writeInvoiceChangeLog({
      invoice: afterInvoice,
      actor,
      changeType: 'update_item',
      details: {
        reason: reason.trim(),
        invoiceItemId,
        before: {
          description: targetItem.invoiceItemDescription,
          amount: Number(targetItem.invoiceItemAmount || 0),
          date: this.normalizeDateOnly(targetItem.invoiceItemDate),
          type: targetItem.invoiceItemType,
        },
        after: normalizedItem,
      },
      previousSnapshot: this.buildInvoiceSnapshot(beforeInvoice),
      newSnapshot: this.buildInvoiceSnapshot(afterInvoice),
    });

    return { invoiceId, total };
  }

  static async deleteInvoiceItem(input: DeleteInvoiceItemInput): Promise<{ invoiceId: string; total: number }> {
    const { invoiceId, invoiceItemId, patientId, actor, reason } = input;
    if (!reason?.trim()) {
      throw new Error('Justificativa é obrigatória para exclusão do item.');
    }

    const beforeInvoice = await this.fetchEditableInvoice(invoiceId, patientId, actor.clientId);
    this.assertAdminActor(actor, beforeInvoice.clientId);

    const targetItem = (beforeInvoice.invoice_items ?? []).find((item: any) => item.invoiceItemId === invoiceItemId);
    if (!targetItem) {
      throw new Error('Item da fatura não encontrado para exclusão.');
    }

    const packageDeletion = await this.assertPackageItemCanBeDeleted({
      clientId: beforeInvoice.clientId,
      patientId: beforeInvoice.patientId,
      targetItem,
    });

    const appointmentCandidates = await this.findInvoiceAppointmentsByDate({
      clientId: beforeInvoice.clientId,
      patientId: beforeInvoice.patientId,
      invoiceId,
      itemDate: targetItem.invoiceItemDate,
    });

    let appointmentUpdate: Record<string, any> = { status: 'not_found' };
    if (appointmentCandidates.length === 1) {
      await this.releaseBilledAppointment(appointmentCandidates[0].appointmentId, beforeInvoice.clientId);
      appointmentUpdate = {
        status: 'released',
        appointmentId: appointmentCandidates[0].appointmentId,
      };
    } else if (appointmentCandidates.length > 1) {
      appointmentUpdate = {
        status: 'ambiguous',
        appointmentIds: appointmentCandidates.map((a) => a.appointmentId),
      };
    }

    const { error: deleteError } = await supabase
      .from('invoice_items')
      .delete()
      .eq('invoiceItemId', invoiceItemId)
      .eq('invoiceId', invoiceId)
      .eq('clientId', beforeInvoice.clientId);

    if (deleteError) {
      throw new Error(`Erro ao excluir item da fatura: ${deleteError.message}`);
    }

    let packageAdjustment: Record<string, any> = { status: 'not_applicable' };
    if (packageDeletion) {
      const nextRemaining = packageDeletion.remaining - packageDeletion.contracted;

      const { error: packageUpdateError } = await supabase
        .from('packages')
        .update({
          packageSessionsRemaining: nextRemaining,
        })
        .eq('packageId', packageDeletion.packageId)
        .eq('clientId', beforeInvoice.clientId);

      if (packageUpdateError) {
        throw new Error(`Erro ao atualizar saldo de sessões do pacote após exclusão do item: ${packageUpdateError.message}`);
      }

      packageAdjustment = {
        status: 'applied',
        packageId: packageDeletion.packageId,
        previousRemaining: packageDeletion.remaining,
        contractedRemoved: packageDeletion.contracted,
        newRemaining: nextRemaining,
      };
    }

    const total = await this.recalculateAndPersistInvoiceTotal(invoiceId, beforeInvoice.clientId);
    const afterInvoice = await this.fetchEditableInvoice(invoiceId, patientId, actor.clientId);

    await this.writeInvoiceChangeLog({
      invoice: afterInvoice,
      actor,
      changeType: 'delete_item',
      details: {
        reason: reason.trim(),
        invoiceItemId,
        deletedItem: {
          description: targetItem.invoiceItemDescription,
          amount: Number(targetItem.invoiceItemAmount || 0),
          date: this.normalizeDateOnly(targetItem.invoiceItemDate),
          type: targetItem.invoiceItemType,
        },
        packageAdjustment,
        appointmentUpdate,
      },
      previousSnapshot: this.buildInvoiceSnapshot(beforeInvoice),
      newSnapshot: this.buildInvoiceSnapshot(afterInvoice),
    });

    return { invoiceId, total };
  }

  static async deleteInvoiceItemForAppointmentReopen(
    input: DeleteInvoiceItemForAppointmentReopenInput,
  ): Promise<{ invoiceId: string; total: number }> {
    const {
      clientId,
      patientId,
      invoiceId,
      invoiceItemId,
      appointmentId,
      actor,
      reason,
    } = input;

    if (!actor?.userId) {
      throw new Error('Usuário executor inválido para estorno de item da fatura.');
    }
    if (!clientId) {
      throw new Error('ClientId é obrigatório para estorno de item da fatura.');
    }
    if (actor.clientId !== clientId) {
      throw new Error('O usuário executor não pertence ao tenant do estorno.');
    }

    const beforeInvoice = await this.fetchEditableInvoice(invoiceId, patientId, clientId);
    if (beforeInvoice.clientId !== clientId || beforeInvoice.patientId !== patientId) {
      throw new Error('Inconsistência de tenant/paciente ao estornar item da fatura.');
    }

    const targetItem = (beforeInvoice.invoice_items ?? []).find(
      (item: any) => item.invoiceItemId === invoiceItemId,
    );
    if (!targetItem) {
      throw new Error('Item da fatura não encontrado para estorno da reabertura.');
    }
    if (targetItem.invoiceItemType !== 'session') {
      throw new Error('Somente itens de sessão podem ser estornados na reabertura do agendamento.');
    }

    const { error: deleteError } = await supabase
      .from('invoice_items')
      .delete()
      .eq('invoiceItemId', invoiceItemId)
      .eq('invoiceId', invoiceId)
      .eq('clientId', clientId);

    if (deleteError) {
      throw new Error(`Erro ao excluir item da fatura no estorno: ${deleteError.message}`);
    }

    const total = await this.recalculateAndPersistInvoiceTotal(invoiceId, clientId);
    const afterInvoice = await this.fetchEditableInvoice(invoiceId, patientId, clientId);

    await this.writeInvoiceChangeLog({
      invoice: afterInvoice,
      actor,
      changeType: 'delete_item',
      details: {
        reason: reason?.trim() || 'Estorno automático ao reabrir agendamento (no_evaluated -> scheduled).',
        source: 'appointment_reopen',
        appointmentId,
        invoiceItemId,
        deletedItem: {
          description: targetItem.invoiceItemDescription,
          amount: Number(targetItem.invoiceItemAmount || 0),
          date: this.normalizeDateOnly(targetItem.invoiceItemDate),
          type: targetItem.invoiceItemType,
        },
      },
      previousSnapshot: this.buildInvoiceSnapshot(beforeInvoice),
      newSnapshot: this.buildInvoiceSnapshot(afterInvoice),
    });

    return { invoiceId, total };
  }

  static async validateDeleteInvoiceItemEligibility(input: {
    invoiceId: string;
    invoiceItemId: string;
    patientId: string;
    actor: InvoiceChangeActor;
  }): Promise<void> {
    const { invoiceId, invoiceItemId, patientId, actor } = input;

    const invoice = await this.fetchEditableInvoice(invoiceId, patientId, actor.clientId);
    this.assertAdminActor(actor, invoice.clientId);

    const targetItem = (invoice.invoice_items ?? []).find((item: any) => item.invoiceItemId === invoiceItemId);
    if (!targetItem) {
      throw new Error('Item da fatura não encontrado para exclusão.');
    }

    await this.assertPackageItemCanBeDeleted({
      clientId: invoice.clientId,
      patientId: invoice.patientId,
      targetItem,
    });
  }

  static async validateReclassifyInvoiceItemEligibility(input: {
    invoiceId: string;
    invoiceItemId: string;
    patientId: string;
    actor: InvoiceChangeActor;
    targetType: "package";
  }): Promise<void> {
    const { invoiceId, invoiceItemId, patientId, actor, targetType } = input;

    const invoice = await this.fetchEditableInvoice(invoiceId, patientId, actor.clientId);
    this.assertAdminActor(actor, invoice.clientId);

    const targetItem = (invoice.invoice_items ?? []).find((item: any) => item.invoiceItemId === invoiceItemId);
    if (!targetItem) {
      throw new Error('Item da fatura não encontrado para reclassificação.');
    }

    const currentType = targetItem.invoiceItemType as InvoiceItem['type'];
    if (targetType !== 'package') {
      throw new Error('Apenas reclassificação Avulsa -> Pacote é permitida.');
    }
    if (currentType !== 'session') {
      throw new Error('Somente itens avulsos (session) podem ser reclassificados para pacote.');
    }

    const appointments = await this.findInvoiceAppointmentsByDate({
      clientId: invoice.clientId,
      patientId: invoice.patientId,
      invoiceId,
      itemDate: targetItem.invoiceItemDate,
    });
    if (appointments.length !== 1) {
      throw new Error('Não foi possível identificar de forma única o agendamento relacionado ao item para reclassificação.');
    }

    const referenceDate = this.currentDateOnly();
    await this.findEligiblePackageForReclassify({
      clientId: invoice.clientId,
      patientId: invoice.patientId,
      referenceDate,
    });
  }

  static async replaceInvoiceItems(input: ReplaceInvoiceItemsInput): Promise<{ invoiceId: string; total: number }> {
    const { invoiceId, patientId, actor, reason, items } = input;
    if (!reason?.trim()) {
      throw new Error('Justificativa é obrigatória para substituição de itens.');
    }

    const beforeInvoice = await this.fetchEditableInvoice(invoiceId, patientId, actor.clientId);
    this.assertAdminActor(actor, beforeInvoice.clientId);

    const normalizedItems = items.map((item) => this.validateInvoiceItemInput(item));

    const { error: deleteError } = await supabase
      .from('invoice_items')
      .delete()
      .eq('invoiceId', invoiceId)
      .eq('clientId', beforeInvoice.clientId);

    if (deleteError) {
      throw new Error(`Erro ao substituir itens da fatura (remoção): ${deleteError.message}`);
    }

    if (normalizedItems.length > 0) {
      const payload = normalizedItems.map((item) => ({
        clientId: beforeInvoice.clientId,
        invoiceId,
        invoiceItemDescription: item.description,
        invoiceItemAmount: item.amount,
        invoiceItemType: item.type,
        invoiceItemDate: item.date,
      }));

      const { error: insertError } = await supabase
        .from('invoice_items')
        .insert(payload);

      if (insertError) {
        throw new Error(`Erro ao substituir itens da fatura (inserção): ${insertError.message}`);
      }
    }

    const total = await this.recalculateAndPersistInvoiceTotal(invoiceId, beforeInvoice.clientId);
    const afterInvoice = await this.fetchEditableInvoice(invoiceId, patientId, actor.clientId);

    await this.writeInvoiceChangeLog({
      invoice: afterInvoice,
      actor,
      changeType: 'replace_items',
      details: {
        reason: reason.trim(),
        replacedItemsCount: normalizedItems.length,
        before: (beforeInvoice.invoice_items ?? []).map((item: any) => ({
          invoiceItemId: item.invoiceItemId,
          description: item.invoiceItemDescription,
          amount: Number(item.invoiceItemAmount || 0),
          date: this.normalizeDateOnly(item.invoiceItemDate),
          type: item.invoiceItemType,
        })),
        after: (afterInvoice.invoice_items ?? []).map((item: any) => ({
          invoiceItemId: item.invoiceItemId,
          description: item.invoiceItemDescription,
          amount: Number(item.invoiceItemAmount || 0),
          date: this.normalizeDateOnly(item.invoiceItemDate),
          type: item.invoiceItemType,
        })),
      },
      previousSnapshot: this.buildInvoiceSnapshot(beforeInvoice),
      newSnapshot: this.buildInvoiceSnapshot(afterInvoice),
    });

    return { invoiceId, total };
  }

  static async reclassifyInvoiceItem(input: ReclassifyInvoiceItemInput): Promise<{ invoiceId: string; total: number }> {
    const { invoiceId, invoiceItemId, patientId, actor, targetType, reason } = input;
    if (!reason?.trim()) {
      throw new Error('Justificativa é obrigatória para reclassificação.');
    }

    const beforeInvoice = await this.fetchEditableInvoice(invoiceId, patientId, actor.clientId);
    this.assertAdminActor(actor, beforeInvoice.clientId);

    const targetItem = (beforeInvoice.invoice_items ?? []).find((item: any) => item.invoiceItemId === invoiceItemId);
    if (!targetItem) {
      throw new Error('Item da fatura não encontrado para reclassificação.');
    }

    const currentType = targetItem.invoiceItemType as InvoiceItem['type'];
    if (targetType !== 'package') {
      throw new Error('Apenas reclassificação Avulsa -> Pacote é permitida.');
    }
    if (currentType !== 'session') {
      throw new Error('Somente itens avulsos (session) podem ser reclassificados para pacote.');
    }

    const appointments = await this.findInvoiceAppointmentsByDate({
      clientId: beforeInvoice.clientId,
      patientId: beforeInvoice.patientId,
      invoiceId,
      itemDate: targetItem.invoiceItemDate,
    });
    if (appointments.length !== 1) {
      throw new Error('Não foi possível identificar de forma única o agendamento relacionado ao item para reclassificação.');
    }
    const appointment = appointments[0];

    const referenceDate = this.currentDateOnly();
    const eligiblePackage = await this.findEligiblePackageForReclassify({
      clientId: beforeInvoice.clientId,
      patientId: beforeInvoice.patientId,
      referenceDate,
    });

    await this.consumePackageSession({
      packageId: eligiblePackage.packageId,
      appointmentId: appointment.appointmentId,
      clientId: beforeInvoice.clientId,
    });

    const nextAmount = 0;
    const packageAction: Record<string, any> = {
      action: 'consume',
      packageId: eligiblePackage.packageId,
      appointmentId: appointment.appointmentId,
    };

    const { error: updateError } = await supabase
      .from('invoice_items')
      .update({
        invoiceItemType: targetType,
        invoiceItemAmount: nextAmount,
      })
      .eq('invoiceItemId', invoiceItemId)
      .eq('invoiceId', invoiceId)
      .eq('clientId', beforeInvoice.clientId);

    if (updateError) {
      throw new Error(`Erro ao reclassificar item da fatura: ${updateError.message}`);
    }

    const total = await this.recalculateAndPersistInvoiceTotal(invoiceId, beforeInvoice.clientId);
    const afterInvoice = await this.fetchEditableInvoice(invoiceId, patientId, actor.clientId);

    await this.writeInvoiceChangeLog({
      invoice: afterInvoice,
      actor,
      changeType: 'reclassify',
      details: {
        reason: reason.trim(),
        invoiceItemId,
        from: currentType,
        to: targetType,
        previousAmount: Number(targetItem.invoiceItemAmount || 0),
        newAmount: nextAmount,
        packageAction,
      },
      previousSnapshot: this.buildInvoiceSnapshot(beforeInvoice),
      newSnapshot: this.buildInvoiceSnapshot(afterInvoice),
    });

    return { invoiceId, total };
  }

  static async deleteMultipleInvoices(invoicesToDelete: Array<{ patientId: string; invoiceId: string }>): Promise<{ success: number; failed: number; errors: string[] }> {
    const message = 'Exclusão de faturas está desabilitada pelas regras de negócio atuais.';
    return {
      success: 0,
      failed: invoicesToDelete.length,
      errors: invoicesToDelete.map((item) => `Fatura ${item.invoiceId}: ${message}`),
    };
  }

  static async deleteInvoice(patientId: string, invoiceId: string): Promise<void> {
    void patientId;
    void invoiceId;
    throw new Error('Exclusão de faturas está desabilitada pelas regras de negócio atuais.');
  }
}
